import { TableProps } from 'antd';
import { Card, Empty } from 'antd/lib';
import Divider from 'antd/lib/divider';
import Table from 'antd/lib/table';
import { getComputerFormattedTime, DEFAULT_DATE_TIME_FORMAT } from '../../domain/date/date';
import { IEventAnalyticGroupedBySite, IEventAnalytic } from '../../domain/event-analytic/interface';
import { metricSorter } from '../../domain/event-analytic/metricSorter';
import { displayMetric } from '../../domain/event-analytic/displayMetric';
import { ActionList } from '../table/actionList/actionList';
import { AreaChartOutlined } from '@ant-design/icons';
import { isNumber } from 'lodash';
import './extra-site-analytic-info.css';
import HistoryOutlined from '@ant-design/icons/lib/icons/HistoryOutlined';

export const ExtraSiteAnalyticInfo = ({
    eventAnalyticBySite,
    onChartOpen,
    onPerformanceHistoryChartOpen,
}: {
    eventAnalyticBySite: IEventAnalyticGroupedBySite;
    onChartOpen: (meterLevelData: IEventAnalytic) => void;
    onPerformanceHistoryChartOpen: (meterLevelData: IEventAnalytic) => void;
}) => {
    const { event_analytic } = eventAnalyticBySite;
    const totalMeters = event_analytic.length;

    const getActions = (item: IEventAnalytic) => {
        return [
            ...(isNumber(item.baseline_kw)
                ? [
                      {
                          title: () => 'Open Chart View',
                          onClick: (record: IEventAnalytic) => () => onChartOpen(record),
                          icon: () => <AreaChartOutlined />,
                      },
                  ]
                : []),
            {
                title: () => 'Open Performance History',
                onClick: (record: IEventAnalytic) => () => onPerformanceHistoryChartOpen(record),
                icon: () => <HistoryOutlined />,
            },
        ];
    };

    const columns: TableProps<IEventAnalytic>['columns'] = [
        {
            title: 'Enrollment ID (SAN)',
            dataIndex: 'service_account_number',
            key: 'service_account_number',
            sorter: (a, b) => a.service_account_number.toLowerCase().localeCompare(b.service_account_number.toLowerCase()),
        },
        {
            title: 'Last Data Update',
            dataIndex: 'date_modified',
            key: 'date_modified',
            render: (date: string | null) => (date ? getComputerFormattedTime(date, DEFAULT_DATE_TIME_FORMAT) : '-'),
            sorter: (a, b) => {
                if (!a.date_modified || !b.date_modified) return 0;

                return new Date(a.date_modified).getTime() - new Date(b.date_modified).getTime();
            },
        },
        {
            title: 'Nomination (kW)',
            dataIndex: 'nomination_kw',
            key: 'nomination_kw',
            sorter: (a, b) => metricSorter(a.nomination_kw, b.nomination_kw),
            render: (text: string, record: IEventAnalytic) => displayMetric(record.nomination_kw),
        },
        {
            title: 'Baseline (kW)',
            dataIndex: 'baseline_kw',
            key: 'baseline_kw',
            sorter: (a, b) => metricSorter(a.baseline_kw, b.baseline_kw),
            render: (text: string, record: IEventAnalytic) => displayMetric(record.baseline_kw),
        },
        {
            title: 'Performance (kW)',
            dataIndex: 'performance_kw',
            key: 'performance_kw',
            sorter: (a, b) => metricSorter(a.performance_kw, b.performance_kw),
            render: (text: string, record: IEventAnalytic) => displayMetric(record.performance_kw),
        },
        {
            key: 'meter_action',
            width: '120px',
            sorter: false,
            render: (_: string, record: IEventAnalytic) => <ActionList actions={getActions(record)} item={record} />,
        },
    ];

    if (!totalMeters) {
        return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />;
    }

    return (
        <>
            <Card className="extra-site-analytic-information" size="small" style={{ padding: '0 16px 16px 16px' }}>
                {!!event_analytic.length && (
                    <>
                        <Divider plain>
                            Detailed Analytic by Meter{totalMeters > 1 ? 's' : ''} ({totalMeters})
                        </Divider>
                        <Table
                            size="small"
                            rowHoverable={false}
                            columns={columns}
                            dataSource={event_analytic}
                            rowKey="service_account_number"
                            pagination={false}
                            className="extra-site-analytic-info-table"
                        />
                    </>
                )}
            </Card>
        </>
    );
};
